import { ContextualTabbedSettings } from '@components'

import { DateTimeFormat, useModal } from '@billy/design-system'
import { Button, Icon, Loader, Switch, Tag, Typography, XStack, YStack } from '@shinetools/lumen-react'
import { Check, CircleAlert, Download, FileKey, RefreshCw } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { EInvoicingDisableModal } from '@features/eInvoicing/elements/EInvoicingDisableModal'
import { useAutomaticEInvoiceSettingToggle } from '@features/eInvoicingSettings/hooks/useAutomaticEInvoiceSettingToggle'
import { PDPNetworkConfig } from '@features/eInvoicingSettings/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../enums/modalId'
import { getCurrentLocale } from '../../../../../utils'
import { EInvoicingSettingsComponent } from '../../NullableEInvoicingSettings'
import { LearnMoreTranslation } from '../elements/LearnMoreTranslation'
import { ActivatePDPEInvoicingCTA } from './elements/ActivatePDPEInvoicingCTA'

const { ContextualSettingInput, CopySettingsButton, MainSettingCta } = ContextualTabbedSettings

export const PDPNetworkSettings: EInvoicingSettingsComponent<PDPNetworkConfig> = ({ networkSettings }) => {
  const { organization } = useUserOrganization()
  const { t } = useTranslation()
  const { config, actions, isLoading } = networkSettings
  const { open: openDisableEInvoicingModal } = useModal(ModalId.DisableEInvoicingModal)
  const {
    eInvoiceIntegrationPdpActivationDate,
    eInvoiceIntegration,
    kycStatus,
    kycCerfaDocument,
    kycUnderReview,
    isUserAuthorizedForKYC: isEInvoiceRegistrationScopeEnabled,
  } = config

  const {
    isAutoCegidIntegrationEnabled,
    isAutoForwardEInvoicesEnabled,
    enable: enableEInvoiceAutoForward,
    disable: disableEInvoiceAutoForward,
  } = useAutomaticEInvoiceSettingToggle()

  const handleAutoForwardEInvoicesChange = useCallback(
    (checked: boolean) => {
      return checked ? enableEInvoiceAutoForward() : disableEInvoiceAutoForward()
    },
    [enableEInvoiceAutoForward, disableEInvoiceAutoForward],
  )

  const locale = getCurrentLocale()
  const addressingLine = eInvoiceIntegration?.scope?.addressingLines?.[0]

  const handleDownloadCerfa = () => {
    if (!kycCerfaDocument) {
      return
    }

    window.open(kycCerfaDocument?.signedUrl, '_blank')
  }

  if (isLoading || !organization) {
    return <ContextualSettingInput isLoading />
  }

  if (kycStatus === 'KYC_REJECTED') {
    return (
      <ActivatePDPEInvoicingCTA organization={organization}>
        <XStack gap={8} align="center">
          <Icon icon={CircleAlert} color="var(--lumen-color-surface-danger-bold-defaut)" />{' '}
          <Typography.Text weight="medium">{t('einvoicing.settings.activation.review_rejected')}</Typography.Text>
        </XStack>
      </ActivatePDPEInvoicingCTA>
    )
  }

  if (kycUnderReview) {
    return (
      <ActivatePDPEInvoicingCTA
        disabled
        tooltip={t('einvoicing.settings.activation.review_in_progress_tooltip')}
        organization={organization}
      >
        <XStack gap={8} align="center">
          <Loader />{' '}
          <Typography.Text weight="medium">{t('einvoicing.settings.activation.review_in_progress')}</Typography.Text>
        </XStack>
      </ActivatePDPEInvoicingCTA>
    )
  }

  if (actions.activate || !eInvoiceIntegration) {
    if (!isEInvoiceRegistrationScopeEnabled) {
      return (
        <ActivatePDPEInvoicingCTA
          disabled
          tooltip={t('einvoicing.settings.activation.activation_forbidden')}
          organization={organization}
        />
      )
    }

    return <ActivatePDPEInvoicingCTA organization={organization} />
  }

  return (
    <YStack gap={16}>
      <MainSettingCta
        description={
          <LearnMoreTranslation
            i18nKey="einvoicing.settings.activation.activate_description"
            linkKey="einvoicing.settings.activation.activate_link"
          />
        }
        ctaContent={
          <XStack gap={8} align="center">
            {/* Without maxheight this component takes the whole height of the flex */}
            <Tag
              icon={<Check />}
              style={{ maxHeight: 'fit-content' }}
              color="success"
              type="subtle"
              size="xsmall"
              label={t('einvoicing.settings.activation.activation_date', {
                date: DateTimeFormat.getFormattedDate(eInvoiceIntegrationPdpActivationDate, locale),
              })}
            />
            <Button
              onClick={() => openDisableEInvoicingModal()}
              disabled={!isEInvoiceRegistrationScopeEnabled}
              variant="outlined"
              label={t('einvoicing.settings.activation.deactivate')}
            />
            <EInvoicingDisableModal shouldNavigateOnSuccess={false} />
          </XStack>
        }
      />

      {addressingLine ? (
        <ContextualSettingInput
          isLoading={isLoading}
          icon={FileKey}
          label={t('einvoicing.settings.fr.addressing_line', { addressingLine })}
          description={
            <LearnMoreTranslation
              i18nKey="einvoicing.settings.fr.addressing_line_hint"
              linkKey="einvoicing.settings.fr.addressing_line_hint_link"
            />
          }
        >
          <CopySettingsButton textToCopy={addressingLine} />
        </ContextualSettingInput>
      ) : null}

      {isAutoCegidIntegrationEnabled ? (
        <ContextualSettingInput
          isLoading={isLoading}
          icon={RefreshCw}
          label={t('einvoicing.settings.fr.auto_forward')}
          description={
            <LearnMoreTranslation
              i18nKey="einvoicing.settings.fr.auto_forward_hint"
              linkKey="einvoicing.settings.fr.auto_forward_hint_link"
            />
          }
        >
          <Switch
            size="compact"
            checked={isAutoForwardEInvoicesEnabled}
            onCheckedChange={handleAutoForwardEInvoicesChange}
          />
        </ContextualSettingInput>
      ) : null}
      {
        // kycCerfa document is allowed to be `null`. Eg in cases where a FR CPA enrolled multiple companies to e-invoicing
        kycCerfaDocument ? ( // Setting the width is necessary, otherwise <a> takes the whole width of the flex, button too
          <Button
            label={t('einvoicing.settings.fr.download_proof')}
            variant="outlined"
            size="small"
            startIcon={<Download />}
            style={{ width: 'min-content' }}
            onClick={handleDownloadCerfa}
          />
        ) : null
      }
    </YStack>
  )
}
